/**
 * Zod Schemas for Client Invites
 *
 * Validation for invite CRUD operations with security hardening
 */

import { z } from 'zod';

// ============================================================================
// Constants
// ============================================================================

export const INVITE_TOKEN_BYTES = 32; // 256 bits of entropy
export const INVITE_TTL_HOURS = 72; // 3 days default
export const RESEND_COOLDOWN_MINUTES = 10;
export const MAX_RESENDS_PER_INVITE = 5;

// ============================================================================
// Invite Status Enum
// ============================================================================

export const InviteStatusEnum = z.enum(['pending', 'accepted', 'expired', 'revoked', 'bounced']);
export type InviteStatus = z.infer<typeof InviteStatusEnum>;

// ============================================================================
// Email Validation & Normalization
// ============================================================================

const emailSchema = z
  .string()
  .email('Invalid email format')
  .max(254, 'Email too long')
  .transform((e) => e.toLowerCase().trim());

// ============================================================================
// Client Invite Schema (database record)
// ============================================================================

export const ClientInviteSchema = z.object({
  id: z.string().min(1).max(100),
  email: emailSchema,
  email_normalized: z.string().max(254),
  email_hash: z.string().length(64), // SHA-256 hex
  token_hash: z.string().length(64), // SHA-256 hex
  clientName: z.string().max(100).optional(),
  coachId: z.string().uuid('Invalid coach ID'),
  orgId: z.string().uuid().optional(),
  status: InviteStatusEnum,
  createdAt: z.string().datetime({ message: 'createdAt must be ISO datetime' }),
  expiresAt: z.string().datetime({ message: 'expiresAt must be ISO datetime' }),
  acceptedAt: z.string().datetime().optional(),
  revokedAt: z.string().datetime().optional(),
  clientId: z.string().uuid().optional(),
  sentCount: z.number().int().min(1).default(1),
  lastSentAt: z.string().datetime().optional(),
  ipHash: z.string().length(64).optional(),
  userAgentHash: z.string().length(64).optional(),
  idempotencyKey: z.string().max(128).optional(),
});

export type ClientInvite = z.infer<typeof ClientInviteSchema>;

// ============================================================================
// Insert Schema (for creating new invites)
// ============================================================================

export const InviteCreateSchema = z.object({
  email: emailSchema,
  clientName: z.string().max(100).trim().optional(),
  coachId: z.string().uuid('Invalid coach ID'),
  ttlHours: z.number().int().min(1).max(168).default(INVITE_TTL_HOURS), // 1h to 7 days
  idempotencyKey: z
    .string()
    .min(8)
    .max(128)
    .regex(/^[a-zA-Z0-9_-]+$/, 'Idempotency key must be alphanumeric with dashes/underscores')
    .optional(),
});

export type InviteCreate = z.infer<typeof InviteCreateSchema>;

// ============================================================================
// Update Schema (partial updates)
// ============================================================================

export const InviteUpdateSchema = z.object({
  status: InviteStatusEnum.optional(),
  acceptedAt: z.string().datetime().nullable().optional(),
  revokedAt: z.string().datetime().nullable().optional(),
  clientId: z.string().uuid().nullable().optional(),
  sentCount: z.number().int().min(1).optional(),
  lastSentAt: z.string().datetime().optional(),
});

export type InviteUpdate = z.infer<typeof InviteUpdateSchema>;

// ============================================================================
// Send Invite Request Schema (API boundary)
// ============================================================================

export const SendInviteRequestSchema = z.object({
  email: emailSchema,
  clientName: z
    .string()
    .max(100, 'Client name too long')
    .trim()
    .optional()
    .transform((name) => (name ? sanitizeForEmail(name) : undefined)),
  coachName: z
    .string()
    .max(100, 'Coach name too long')
    .trim()
    .optional()
    .transform((name) => (name ? sanitizeForEmail(name) : undefined)),
  ttlHours: z.number().int().min(1).max(168).default(INVITE_TTL_HOURS),
});

export type SendInviteRequest = z.infer<typeof SendInviteRequestSchema>;

// ============================================================================
// Resend Invite Request Schema
// ============================================================================

export const ResendInviteRequestSchema = z.object({
  inviteId: z.string().min(1).max(100),
});

export type ResendInviteRequest = z.infer<typeof ResendInviteRequestSchema>;

// ============================================================================
// Revoke Invite Request Schema
// ============================================================================

export const RevokeInviteRequestSchema = z.object({
  inviteId: z.string().min(1).max(100),
  reason: z.string().max(500).optional(),
});

export type RevokeInviteRequest = z.infer<typeof RevokeInviteRequestSchema>;

// ============================================================================
// Token Redemption Schema
// ============================================================================

export const RedeemTokenRequestSchema = z.object({
  token: z.string().min(32).max(128), // Base64url encoded token
  email: emailSchema,
});

export type RedeemTokenRequest = z.infer<typeof RedeemTokenRequestSchema>;

// ============================================================================
// Rate Limit Response Schema
// ============================================================================

export const RateLimitInfoSchema = z.object({
  coachCount24h: z.number().int(),
  emailCount24h: z.number().int(),
  canSend: z.boolean(),
});

export type RateLimitInfo = z.infer<typeof RateLimitInfoSchema>;

// ============================================================================
// Idempotency Key Schema
// ============================================================================

export const IdempotencyKeySchema = z
  .string()
  .min(8, 'Idempotency key must be at least 8 characters')
  .max(128, 'Idempotency key must be at most 128 characters')
  .regex(/^[a-zA-Z0-9_-]+$/, 'Idempotency key must be alphanumeric with dashes/underscores');

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Sanitize user input for safe inclusion in emails
 * Removes HTML tags and dangerous characters
 */
function sanitizeForEmail(input: string): string {
  return input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>"'&]/g, '') // Remove dangerous chars
    .trim()
    .slice(0, 100);
}

/**
 * Mask email for display (e.g., "t***@example.com")
 */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***@***';

  const masked = local.length > 2 ? local[0] + '***' + local[local.length - 1] : '***';

  return `${masked}@${domain}`;
}

/**
 * Generate idempotency key from deterministic inputs
 */
export function generateIdempotencyKey(coachId: string, email: string, action: string): string {
  const timestamp = Math.floor(Date.now() / (1000 * 60 * 60)); // Hour-level granularity
  return `${action}-${coachId.slice(0, 8)}-${timestamp}`;
}
